import { FC, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Container, makeStyles, Theme, Grid, Collapse } from '@material-ui/core';
import { ArrowDown, LineIcon, PlusIcon } from '../../../svgs/gpm';
import { useDarkMode, useMediaUp } from '../../../hooks';

interface IndustriesServedProps {
  categories: Array<{ title: string; text: string; link: string }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginBottom: 80,
    marginTop: 80,
    [theme.breakpoints.up('md')]: {
      marginBottom: 150,
    },
  },
  box: {
    backgroundColor: '#F1F1F1',
    borderRadius: 10,
    marginTop: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F1F1F1',
      color: '#116666',
    },
  },
  title: {
    color: '#116666',
    fontSize: 15,
    fontWeight: 700,
  },
  activeBox: {
    backgroundColor: '#116666',
    borderRadius: 10,
    marginTop: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#116666',
      color: theme.palette.common.white,
    },
  },
  activeTitle: {
    color: theme.palette.common.white,
    fontSize: 15,
    fontWeight: 700,
  },
  mainTitle: {
    color: '#116666',
    fontSize: 40,
    fontWeight: 700,
    marginBottom: theme.spacing(3),
  },
  subTitle: {
    color: (isDarkMode) => (isDarkMode ? theme.palette.common.white : '#0A1524'),
    fontSize: 20,
    fontWeight: 500,
    marginBottom: theme.spacing(3),
  },
  text: {
    color: (isDarkMode) => (isDarkMode ? theme.palette.common.white : '#0A1524'),
    fontSize: 10,
    fontWeight: 500,
    maxWidth: 344,
    marginBottom: 52,
  },
  link: {
    boxShadow: 'none',
    borderRadius: '0 10px',
    padding: '14px 35px',
    textTransform: 'none',
    fontSize: 15,
    lineHeight: 1,
    fontWeight: 500,
    color: '#116666',
    border: '2px solid #116666',
    backgroundColor: 'transparent',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#116666',
      color: '#ffffff',
    },
  },
  select: {
    width: '100%',
    marginBottom: theme.spacing(2),
    backgroundColor: '#F1F1F1',
    borderRadius: 10,
    padding: theme.spacing(1.5),
    fontWeight: 700,
    color: '#116666',
    '& .MuiSelect-icon': {
      color: '#116666',
    },
  },
  mobileBox: {
    backgroundColor: '#116666',
    borderRadius: 10,
    height: 56,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    '&:hover': {
      backgroundColor: '#116666',
      color: theme.palette.common.white,
    },
  },
  iconClose: {
    transform: 'rotate(180deg)',
    transition: 'transform 0.3s ease-out',
  },
  icon: {
    transition: 'transform 0.3s ease-out',
  },
}));

const IndustriesServed: FC<IndustriesServedProps> = ({ categories }) => {
  const [selected, setSelected] = useState(categories[0]);
  const { isDarkMode } = useDarkMode();
  const classes = useStyles(isDarkMode);
  const [open, setOpen] = useState(false);

  const isSmallUp = useMediaUp('sm');

  const handleSelect = (category: typeof categories[0]) => {
    setSelected(category);
    setOpen(false);
  };

  return (
    <Container maxWidth={false} fixed>
      <Grid className={classes.container} container spacing={6}>
        {/* Left Side Navigation */}
        <Grid item md={6} xs={12}>
          {!isSmallUp ? (
            <>
              <Box className={classes.mobileBox} onClick={() => setOpen((prev) => !prev)}>
                <Typography className={classes.activeTitle}>{selected.title}</Typography>
                {open ? <ArrowDown className={classes.iconClose} /> : <ArrowDown className={classes.icon} />}
              </Box>
              <Collapse in={open}>
                <List component="nav">
                  {categories.map((category, idx) => (
                    <ListItem
                      key={idx}
                      button
                      onClick={() => handleSelect(category)}
                      className={selected === category ? classes.activeBox : classes.box}
                    >
                      <ListItemText
                        primary={
                          <Typography className={selected === category ? classes.activeTitle : classes.title}>
                            {category.title}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          ) : (
            <List>
              {categories.map((category, idx) => (
                <ListItem
                  className={selected === category ? classes.activeBox : classes.box}
                  key={idx}
                  button
                  onClick={() => setSelected(category)}
                >
                  <ListItemText>
                    <Typography className={selected === category ? classes.activeTitle : classes.title}>
                      {category.title}
                    </Typography>
                  </ListItemText>
                  <PlusIcon />
                </ListItem>
              ))}
            </List>
          )}
        </Grid>

        {/* Right Side Content */}
        <Grid item md={6} xs={12}>
          <Box position="relative">
            <Typography className={classes.mainTitle}>Industries Served</Typography>
            <Typography className={classes.subTitle}>{selected.title}</Typography>
            <Typography className={classes.text}>{selected.text}</Typography>
            <a href={selected.link} className={classes.link}>
              {'Read More'}
            </a>
            {isSmallUp && (
              <Box position={'absolute'} right={0} top={290}>
                <LineIcon />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default IndustriesServed;
